/**
 * Drama 页数据结构与接口映射
 * 数据源：GET /interactive/book/video-bridge/list（按 Nacos 配置顺序返回完整详情，
 * 命中桥接的条目追加 play_mode / video_entry），一期只取第一条。
 */
import type { VideoBridgeEntry } from '@/utils/videoBridge'
import { dramaLocalEpisode, isVideoBridge, parseVideoBridgeCast, resolveVideoBridgeUrl, videoBridgeVideoUrl } from '@/utils/videoBridge'

export type DramaWatchStatus = 'not_started' | 'in_progress' | 'completed'

export interface DramaCharacter {
  name: string
  /** 首页卡片副标题，无则不展示 */
  subtitle?: string
  /** 弹窗内身份标签，无则不展示 */
  tags?: string[]
  /** 人物简介（弹窗内展示） */
  desc: string
  avatar: string
}

export interface DramaTag {
  name: string
  hot?: boolean
  highlight?: boolean
}

export interface DramaInfo {
  id: string
  title: string
  videoUrl: string
  coverUrl: string
  watchStatus: DramaWatchStatus
  currentEpisode: number
  totalEpisodes: number
  progressPercent: number
  unlockedEndings: number
  tags: DramaTag[]
  storyLine: string
  characters: DramaCharacter[]
}

/** 桥接列表条目（详情对象 + 桥接字段），字段以真实接口为准 */
export interface VideoBridgeListItem {
  contentId: string
  contentName: string
  coverUrl?: string
  landscapeCoverUrl?: string
  introduce?: string
  chapterNum?: number
  finishStatus?: number
  currentChapterId?: string
  chapters?: { chapterId: string; sequenceNo: number }[]
  tagList?: { id?: string; name?: string }[]
  play_mode?: string
  video_entry?: VideoBridgeEntry
}

/** video_entry.cast_json → 人物资料，顺序以接口下发为准；头像路径相对 oss_root */
function mapCharacters(entry: VideoBridgeEntry | undefined): DramaCharacter[] {
  return parseVideoBridgeCast(entry).map(cast => ({
    name: (cast.actor || '').toUpperCase(),
    subtitle: cast.short_intro_en || undefined,
    tags: Array.isArray(cast.tags) ? cast.tags.filter(Boolean) : undefined,
    desc: cast.intro ?? '',
    avatar: resolveVideoBridgeUrl(entry, cast.path),
  }))
}

/**
 * 桥接列表条目 → Drama 页展示数据；未命中桥接返回 null
 * 观看进度后端暂未下发，与 Library 页口径一致：读播放器本地存档推算已看到的最大集数，
 * > 0 视为 in_progress（CTA=Continue Ep.N）。completed/进度百分比/已解锁结局数待后端字段就绪后补充。
 */
export function mapVideoBridgeItem(item: VideoBridgeListItem | undefined): DramaInfo | null {
  if (!item || !isVideoBridge(item))
    return null

  const entry = item.video_entry
  const totalEpisodes = item.chapterNum ?? 0
  const localEpisode = dramaLocalEpisode()
  // 已开始封顶总集数；未开始回退到接口 currentChapterId 对应集数，再退到第 1 集
  const serverEpisode = item.chapters?.find(c => c.chapterId === item.currentChapterId)?.sequenceNo ?? 1
  const currentEpisode = localEpisode > 0
    ? (totalEpisodes ? Math.min(localEpisode, totalEpisodes) : localEpisode)
    : serverEpisode

  return {
    id: item.contentId,
    title: item.contentName,
    videoUrl: videoBridgeVideoUrl(entry),
    coverUrl: item.landscapeCoverUrl || item.coverUrl || '',
    watchStatus: localEpisode > 0 ? 'in_progress' : 'not_started',
    currentEpisode,
    totalEpisodes,
    progressPercent: 0,
    unlockedEndings: 0,
    // 标签无高亮字段，按设计稿首个标签高亮
    tags: (item.tagList ?? [])
      .filter(tag => tag.name)
      .map((tag, index) => ({ name: tag.name!, highlight: index === 0 })),
    storyLine: item.introduce ?? '',
    characters: mapCharacters(entry),
  }
}
